import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { autocompletePartners, readPartnersByBranch } from "../../services/partner.service.ts";
import { readBranches } from "../../services/branch.service.ts";
import { createDirectBoss, updateDirectBoss, DirectBoss, fetchDirectBosses } from "../../services/direct-boss.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import { DirectBossSchema } from "../../validation/schemas/DirectBossSchema.ts";

interface Partner {
  id: number;
  person: {
    firstName: string;
    lastName: string;
    secondLastName?: string;
  };
  candidate?: {
    shift: {
      name: string;
    };
    vacancy: {
      position: {
        name: string;
      };
    };
    branch: {
      id: number;
      name: string;
    };
  };
}

interface Branch {
  id: number;
  name: string;
}

interface DirectBossFormProps {
  close: () => void;
  onSuccess: () => void;
  directBossToEdit?: DirectBoss | null;
  isEditing?: boolean;
}

interface DirectBossFormValues {
  bossId: number | null;
  employeeIds: number[];
  description: string;
}

const DirectBossForm = ({ close, onSuccess, directBossToEdit, isEditing = false }: DirectBossFormProps) => {

  const [partners, setPartners] = useState<Partner[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [selectedBranchEmployees, setSelectedBranchEmployees] = useState<number | null>(null);
  
  // Estados para autocomplete del jefe
  const [bossSearch, setBossSearch] = useState("");
  const [filteredBosses, setFilteredBosses] = useState<Partner[]>([]);
  const [showBossDropdown, setShowBossDropdown] = useState(false);
  const [bossSearchLoading, setBossSearchLoading] = useState(false);
  const bossContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Estados para autocomplete de empleados
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState<Partner[]>([]);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const employeeContainerRef = useRef<HTMLDivElement | null>(null);
  const [selectedEmployees, setSelectedEmployees] = useState<Partner[]>([]);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [reservedEmployeeIds, setReservedEmployeeIds] = useState<Set<number>>(
    new Set()
  );

  const [loading, setLoading] = useState(false);
  const [timeoutId, setTimeoutId] = useState<any>(null);

  const getCurrentEditEmployeeIds = React.useCallback(() => {
    if (!isEditing || !directBossToEdit) return new Set<number>();
    return new Set((directBossToEdit.employees || []).map((emp) => Number(emp.id)));
  }, [isEditing, directBossToEdit]);

  const isEmployeeBlockedForOtherBoss = React.useCallback(
    (employeeId: number) => {
      const currentEditEmployeeIds = getCurrentEditEmployeeIds();
      if (currentEditEmployeeIds.has(Number(employeeId))) return false;
      return reservedEmployeeIds.has(Number(employeeId));
    },
    [reservedEmployeeIds, getCurrentEditEmployeeIds]
  );

  const filterBlockedEmployees = React.useCallback(
    (employeeList: Partner[]) => {
      return (employeeList || []).filter(
        (employee) => !isEmployeeBlockedForOtherBoss(Number(employee.id))
      );
    },
    [isEmployeeBlockedForOtherBoss]
  );

  // Función para detectar si todos los colaboradores son de la misma sucursal
  const getCommonBranch = () => {
    if (selectedEmployees.length === 0) return null;
    
    const branches = selectedEmployees.map(emp => emp.candidate?.branch?.id).filter(Boolean);
    if (branches.length === 0) return null;
    
    const uniqueBranches = [...new Set(branches)];
    return uniqueBranches.length === 1 ? uniqueBranches[0] : null;
  };

  const commonBranch = getCommonBranch();
  
  // Verificar si estamos en modo "Todas las sucursales" con colaboradores de la misma sucursal
  const isAllBranchesWithCommonEmployees = selectedBranchEmployees === null && commonBranch !== null;
  
  // Verificar si hay colaboradores de diferentes sucursales (bloquear select)
  const hasMixedBranches = selectedEmployees.length > 0 && commonBranch === null;

  const formik = useFormik<DirectBossFormValues>({
    initialValues: {
      bossId: null,
      employeeIds: [],
      description: "",
    },
    validationSchema: DirectBossSchema,
    onSubmit: async (values) => {
      // Validar que se haya seleccionado un jefe
      if (!values.bossId || values.bossId === null) {
        notification("Error", "Debe seleccionar un jefe directo de la lista", "danger");
        return;
      }
      
      setLoading(true);
      try {
        if (isEditing && directBossToEdit) {
          await updateDirectBoss(directBossToEdit.id, values);
          notification("Éxito", "Jefe directo actualizado correctamente", "success");
        } else {
          await createDirectBoss(values);
          notification("Éxito", "Jefe directo creado correctamente", "success");
        }
        onSuccess();
      } catch (error) {
        console.error("Error saving direct boss:", error);
        notification("Error", "No se pudo guardar el jefe directo", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  // Inicializar datos si estamos editando
  useEffect(() => {
    if (isEditing && directBossToEdit) {
      formik.setValues({
        bossId: directBossToEdit.boss.id,
        employeeIds: directBossToEdit.employees.map(emp => emp.id),
        description: directBossToEdit.description || "",
      });
      
      // Establecer empleados seleccionados
      setSelectedEmployees(directBossToEdit.employees);
      
      // Establecer búsqueda del jefe
      setBossSearch(`${directBossToEdit.boss.person.firstName} ${directBossToEdit.boss.person.lastName}`);
    }
  }, [isEditing, directBossToEdit]);

  const fetchBranches = async () => {
    try {
      const resp = await readBranches();
      setBranches(resp || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchReservedEmployees = React.useCallback(async () => {
    try {
      const response = await fetchDirectBosses(`?limit=500&offset=0&isActive=true`);
      const rows = response?.results || [];
      const reserved = new Set<number>();
      rows.forEach((directBoss: DirectBoss) => {
        if (isEditing && directBossToEdit && Number(directBoss.id) === Number(directBossToEdit.id)) {
          return;
        }
        (directBoss.employees || []).forEach((employee: any) => {
          if (employee?.id) reserved.add(Number(employee.id));
        });
      });
      setReservedEmployeeIds(reserved);
    } catch (error) {
      console.error("Error fetching reserved employees from direct bosses:", error);
      setReservedEmployeeIds(new Set());
    }
  }, [isEditing, directBossToEdit]);

  const fetchPartners = async (searchTerm: string = "", branchId?: number) => {
    try {
      const resp = await autocompletePartners(searchTerm, branchId);
      return resp || [];
    } catch (error) {
      console.error("Error fetching partners:", error);
      return [];
    }
  };

  const handleBossSearch = async (searchTerm: string) => {
    setBossSearch(searchTerm);
    setShowBossDropdown(true);

    if (timeoutId) clearTimeout(timeoutId);

    const newTimeoutId = setTimeout(async () => {
      try {
        setBossSearchLoading(true);
        // No filtrar por sucursal para el jefe directo - mostrar todos
        const data = await fetchPartners(searchTerm);
        // Filtrar empleados seleccionados para que no aparezcan como jefe
        const employeeIds = selectedEmployees.map(emp => emp.id);
        const filteredData = data.filter(boss => !employeeIds.includes(boss.id));
        setFilteredBosses(filteredData);
      } catch (error) {
        console.error("Error searching bosses:", error);
      } finally {
        setBossSearchLoading(false);
      }
    }, 500);

    setTimeoutId(newTimeoutId);
  };

  const handleEmployeeSearch = async (searchTerm: string) => {
    setEmployeeSearch(searchTerm);
    setShowEmployeeDropdown(true);
    
    // Resetear el modo "Seleccionar todos" cuando se hace una nueva búsqueda
    if (selectAllMode) {
      setSelectAllMode(false);
    }

    if (timeoutId) clearTimeout(timeoutId);

    const newTimeoutId = setTimeout(async () => {
      try {
        setEmployeeSearchLoading(true);
        const data = await fetchPartners(searchTerm, selectedBranchEmployees || undefined);
        // Filtrar el jefe seleccionado + empleados ya reservados en otros jefes
        const filteredData = filterBlockedEmployees(
          data.filter(employee => employee.id !== formik.values.bossId)
        );
        setFilteredEmployees(filteredData);
      } catch (error) {
        console.error("Error searching employees:", error);
      } finally {
        setEmployeeSearchLoading(false);
      }
    }, 500);

    setTimeoutId(newTimeoutId);
  };

  const handleBossSelect = (boss: Partner) => {
    formik.setFieldValue('bossId', boss.id);
    formik.setFieldTouched('bossId', true);
    setBossSearch(`${boss.person.firstName} ${boss.person.lastName}`);
    setShowBossDropdown(false);
  };

  const handleEmployeeToggle = (employee: Partner) => {
    const isSelected = selectedEmployees.some(emp => emp.id === employee.id);
    
    if (isSelected) {
      const newEmployees = selectedEmployees.filter(emp => emp.id !== employee.id);
      setSelectedEmployees(newEmployees);
      formik.setFieldValue('employeeIds', newEmployees.map(emp => emp.id));
    } else {
      const newEmployees = [...selectedEmployees, employee];
      setSelectedEmployees(newEmployees);
      formik.setFieldValue('employeeIds', newEmployees.map(emp => emp.id));
    }
    setSelectAllMode(false);
  };

  const handleSelectAllEmployees = async () => {
    // Verificar si todos los empleados filtrados ya están seleccionados
    const allFilteredSelected = filteredEmployees.every(emp => 
      selectedEmployees.some(selected => selected.id === emp.id)
    );
    
    if (allFilteredSelected) {
      // Deseleccionar todos los empleados filtrados
      const filteredEmployeeIds = filteredEmployees.map(emp => emp.id);
      const remainingEmployees = selectedEmployees.filter(emp => !filteredEmployeeIds.includes(emp.id));
      setSelectedEmployees(remainingEmployees);
      formik.setFieldValue('employeeIds', remainingEmployees.map(emp => emp.id));
    } else {
      // Si hay una sucursal específica seleccionada, obtener TODOS los colaboradores de esa sucursal
      if (selectedBranchEmployees) {
        try {
          setLoading(true);
          const allBranchEmployees = await readPartnersByBranch(selectedBranchEmployees);
          
          const filteredBranchEmployees = filterBlockedEmployees(
            allBranchEmployees.filter(employee => employee.id !== formik.values.bossId)
          );
          const newEmployees = [...selectedEmployees];
          filteredBranchEmployees.forEach(emp => {
            if (!newEmployees.some(selected => selected.id === emp.id)) {
              newEmployees.push(emp);
            }
          });
          
          setSelectedEmployees(newEmployees);
          formik.setFieldValue('employeeIds', newEmployees.map(emp => emp.id));
          
          notification(
            "Éxito", 
            `${filteredBranchEmployees.length} colaboradores de la sucursal seleccionados`, 
            "success"
          );
        } catch (error) {
          console.error("Error loading branch employees:", error);
          notification("Error", "No se pudieron cargar los colaboradores de la sucursal", "danger");
        } finally {
          setLoading(false);
        }
      } else {
        const newEmployees = [...selectedEmployees];
        filteredEmployees.forEach(emp => {
          if (!newEmployees.some(selected => selected.id === emp.id)) {
            newEmployees.push(emp);
          }
        });
        setSelectedEmployees(newEmployees);
        formik.setFieldValue('employeeIds', newEmployees.map(emp => emp.id));
      }
    }
  };

  const handleBranchChange = (branchId: number | null) => {
    setSelectedBranch(branchId);
    // No limpiar la búsqueda del jefe ya que no se filtra por sucursal
    // setFormData(prev => ({ ...prev, bossId: null }));
    // setBossSearch("");
    // setFilteredBosses([]);
  };

  const handleBranchEmployeesChange = async (branchId: number | null) => {
    setSelectedBranchEmployees(branchId);
    
    // Cargar empleados iniciales para la nueva sucursal
    try {
      const data = await fetchPartners("", branchId || undefined);
      const initialEmployees = filterBlockedEmployees(data).slice(0, 5);
      setFilteredEmployees(initialEmployees);
    } catch (error) {
      console.error("Error loading employees for branch:", error);
      setFilteredEmployees([]);
    }
  };


  useEffect(() => {
    fetchBranches();
    fetchReservedEmployees();
    // Cargar empleados iniciales para mostrar que funciona
    loadInitialEmployees();
  }, []);

  const loadInitialEmployees = async () => {
    try {
      const data = await fetchPartners("", selectedBranchEmployees || undefined);
      // Mostrar solo los primeros 5 empleados
      const initialEmployees = filterBlockedEmployees(data).slice(0, 5);
      setFilteredEmployees(initialEmployees);
    } catch (error) {
      console.error("Error loading initial employees:", error);
    }
  };

  useEffect(() => {
    fetchReservedEmployees();
  }, [fetchReservedEmployees]);

  useEffect(() => {
    // Refrescar lista visible cuando cambia el bloqueo global
    setFilteredEmployees((prev) => filterBlockedEmployees(prev));
  }, [filterBlockedEmployees]);

  // Actualizar búsquedas cuando cambie la selección para evitar redundancia
  useEffect(() => {
    if (bossSearch && filteredBosses.length > 0) {
      const employeeIds = selectedEmployees.map(emp => emp.id);
      const filteredData = filteredBosses.filter(boss => !employeeIds.includes(boss.id));
      setFilteredBosses(filteredData);
    }
  }, [selectedEmployees]);

  useEffect(() => {
    if (employeeSearch && filteredEmployees.length > 0) {
      const filteredData = filteredEmployees.filter(employee => employee.id !== formik.values.bossId);
      setFilteredEmployees(filteredData);
    }
  }, [formik.values.bossId]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const bossEl = bossContainerRef.current;
      const employeeEl = employeeContainerRef.current;
      
      if (bossEl && !bossEl.contains(event.target) && 
          !event.target.closest('.boss-dropdown-item')) {
        setShowBossDropdown(false);
      }
      
      if (employeeEl && !employeeEl.contains(event.target) && 
          !event.target.closest('.employee-dropdown-item')) {
        setShowEmployeeDropdown(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="p-4">
      <form onSubmit={formik.handleSubmit}>
        <div className="row g-3">
          <div className="col-md-12">
            <label className="form-label">Jefe directo *</label>
           
            <div className="position-relative" ref={bossContainerRef}>
              <div className="input-group">
                <div
                  className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                  style={{ zIndex: 10 }}
                >
                  {bossSearchLoading ? (
                    <i className="fa fa-spinner fa-spin" />
                  ) : (
                    <i className="fa fa-user-tie opacity-5" />
                  )}
                </div>
                <input
                  type="text"
                  className={`form-control px-35px bg-light ${formik.touched.bossId && formik.errors.bossId ? 'is-invalid' : ''}`}
                  placeholder="Buscar jefe..."
                  value={bossSearch}
                  onChange={(e) => handleBossSearch(e.target.value)}
                  onFocus={() => setShowBossDropdown(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setShowBossDropdown(false);
                  }}
                />
              </div>
              {showBossDropdown && filteredBosses.length > 0 && (
                <div
                  className="position-absolute w-100 bg-white border rounded shadow-sm"
                  style={{
                    zIndex: 1000,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  {filteredBosses.map((boss) => (
                    <div
                      key={boss.id}
                      className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center boss-dropdown-item"
                      onClick={() => handleBossSelect(boss)}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f8f9fa")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "white")
                      }
                    >
                      <i className="fa fa-user-tie me-2 text-primary"></i>
                      {boss.person.firstName} {boss.person.lastName}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {formik.touched.bossId && formik.errors.bossId && (
              <div className="text-danger small mb-1">{formik.errors.bossId}</div>
            )}

          </div>

          <div className="col-md-12">
            <label className="form-label">Descripción</label>
            <textarea
              className={`form-control ${formik.touched.description && formik.errors.description ? 'is-invalid' : ''}`}
              rows={3}
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Descripción opcional del jefe directo"
            />
            {formik.touched.description && formik.errors.description && (
              <div className="invalid-feedback">
                {formik.errors.description}
              </div>
            )}
          </div>

          <div className="col-12">
            <label className="form-label mb-4">Empleados bajo su autoridad *</label>
           
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <select
                  className="form-select form-select-sm"
                  value={selectedBranchEmployees || ""}
                  onChange={(e) => handleBranchEmployeesChange(e.target.value ? Number(e.target.value) : null)}
                  disabled={hasMixedBranches || (selectedBranchEmployees !== null && selectedEmployees.length > 0)}
                >
                  <option value="">Todas las sucursales</option>
                  {branches.map((branch) => {
                    // Si estamos en "Todas las sucursales" con colaboradores de la misma sucursal,
                    // solo mostrar "Todas las sucursales" y la sucursal común
                    if (isAllBranchesWithCommonEmployees) {
                      if (branch.id === commonBranch) {
                        return (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        );
                      }
                      return null; // No mostrar otras sucursales
                    }
                    
                    // Si hay colaboradores de diferentes sucursales, no mostrar ninguna opción adicional
                    if (hasMixedBranches) {
                      return null;
                    }
                    
                    // Comportamiento normal para otros casos
                    return (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="col-md-8">
                <div className="position-relative" ref={employeeContainerRef}>
              <div className="input-group">
                <div
                  className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                  style={{ zIndex: 10 }}
                >
                  {employeeSearchLoading ? (
                    <i className="fa fa-spinner fa-spin" />
                  ) : (
                    <i className="fa fa-users opacity-5" />
                  )}
                </div>
                <input
                  type="text"
                  className="form-control px-35px bg-light"
                  placeholder={
                    selectedEmployees.length > 0 
                      ? `Buscar empleados... (${selectedEmployees.length} seleccionados)`
                      : "Buscar empleados..."
                  }
                  value={employeeSearch}
                  onChange={(e) => handleEmployeeSearch(e.target.value)}
                  onFocus={() => setShowEmployeeDropdown(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setShowEmployeeDropdown(false);
                  }}
                />
              </div>
              {showEmployeeDropdown && filteredEmployees.length > 0 && (
                <div
                  className="position-absolute w-100 bg-white border rounded shadow-sm"
                  style={{
                    zIndex: 1000,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  {/* Opción "Seleccionar/Deseleccionar todos" */}
                  {filteredEmployees.length > 0 && (() => {
                    const allFilteredSelected = filteredEmployees.every(emp => 
                      selectedEmployees.some(selected => selected.id === emp.id)
                    );
                    
                    return (
                      <div
                        className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center employee-dropdown-item"
                        style={{ 
                          cursor: "pointer",
                          backgroundColor: allFilteredSelected ? "#e3f2fd" : "white"
                        }}
                        onClick={handleSelectAllEmployees}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = allFilteredSelected ? "#e3f2fd" : "#f8f9fa")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = allFilteredSelected ? "#e3f2fd" : "white")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={() => {}}
                          className="me-2"
                          style={{ width: 16, height: 16 }}
                        />
                        <strong className={allFilteredSelected ? "text-warning" : "text-primary"}>
                          <i className={`fa ${allFilteredSelected ? 'fa-minus' : 'fa-plus'} me-1`}></i>
                          {allFilteredSelected 
                            ? `Deseleccionar todos (${selectedEmployees.length} seleccionados)` 
                            : selectedBranchEmployees 
                              ? `Agregar todos de la sucursal (${selectedEmployees.length} seleccionados)` 
                              : `Agregar todos (${selectedEmployees.length} seleccionados)`
                          }
                        </strong>
                      </div>
                    );
                  })()}
                  
                  {/* Lista de empleados individuales */}
                  {filteredEmployees.map((employee) => (
                    <div
                      key={employee.id}
                      className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center employee-dropdown-item"
                      style={{ 
                        cursor: "pointer",
                        backgroundColor: selectedEmployees.some(emp => emp.id === employee.id) ? "#e3f2fd" : "white"
                      }}
                      onClick={() => handleEmployeeToggle(employee)}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = selectedEmployees.some(emp => emp.id === employee.id) ? "#e3f2fd" : "#f8f9fa")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = selectedEmployees.some(emp => emp.id === employee.id) ? "#e3f2fd" : "white")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmployees.some(emp => emp.id === employee.id)}
                        onChange={() => {}}
                        className="me-2"
                        style={{ width: 16, height: 16 }}
                      />
                      <i className="fa fa-user me-2 text-info"></i>
                      {employee.person.firstName} {employee.person.lastName}
                    </div>
                  ))}
                </div>
              )}
                </div>
              </div>
            </div>
            {formik.touched.employeeIds && formik.errors.employeeIds && (
              <div className="text-danger small mb-1">{formik.errors.employeeIds}</div>
            )}
          </div>

          {/* Container de colaboradores seleccionados */}
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label mb-0">Colaboradores asociados</label>
              {selectedEmployees.length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => {
                    setSelectedEmployees([]);
                    formik.setFieldValue('employeeIds', []);
                    setSelectAllMode(false);
                    // Si había colaboradores de "Todas las sucursales", resetear el filtro
                    if (selectedBranchEmployees === null) {
                      setSelectedBranchEmployees(null);
                    }
                  }}
                  title="Limpiar todos los colaboradores"
                >
                  <i className="fa fa-trash me-1"></i>
                  Limpiar
                </button>
              )}
            </div>
            <div className="card border">
              <div className="card-body p-3" style={{ height: '250px', overflowY: 'auto' }}>
                {selectedEmployees.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '4%' }}>#</th>
                          <th style={{ width: '18%' }}>Nombre</th>
                          <th style={{ width: '18%' }}>Apellidos</th>
                          <th style={{ width: '18%' }}>Sucursal</th>
                          <th style={{ width: '18%' }}>Puesto</th>
                          <th style={{ width: '14%' }}>Turno</th>
                          <th style={{ width: '10%' }} className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployees.map((employee, index) => (
                          <tr key={employee.id}>
                            <td className="text-muted">{index + 1}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="fa fa-user-circle text-primary me-2"></i>
                                <span className="fw-medium">
                                  {employee.person.firstName}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="text-muted">
                                {employee.person.lastName} {employee.person.secondLastName || ''}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-secondary">
                                <i className="fa fa-building me-1"></i>
                                {employee.candidate?.branch?.name || 'Sin sucursal'}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-primary">
                                <i className="fa fa-briefcase me-1"></i>
                                {employee.candidate?.vacancy?.position?.name || 'Sin puesto'}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-info">
                                <i className="fa fa-clock me-1"></i>
                                {employee.candidate?.shift?.name || 'Sin turno'}
                              </span>
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleEmployeeToggle(employee)}
                                title="Remover colaborador"
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="d-flex flex-column justify-content-center align-items-center h-100">
                    <div className="mb-3">
                      <i className="fa fa-users text-muted" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h6 className="text-muted mb-2">Sin colaboradores asociados</h6>
                    <p className="text-muted small mb-0 text-center">
                      Selecciona colaboradores usando el filtro y búsqueda de arriba
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Consejos e información */}
        <div className="alert alert-info mt-3">
          <div className="d-flex align-items-start">
            <i className="fa fa-info-circle me-2 mt-1"></i>
            <div>
              <strong>Consejos:</strong>
              <ul className="mb-0 mt-2">
                <li><strong>Búsqueda:</strong> Se muestran 5 empleados iniciales, escribe para buscar más</li>
                <li><strong>Sucursal:</strong> Una vez que agregues empleados de una sucursal específica, no podrás cambiar a otra</li>
                <li><strong>Mezclar sucursales:</strong> Usa "Todas las sucursales" desde el inicio</li>
                <li><strong>Agregar:</strong> Usa "Agregar todos" para seleccionar todos los resultados de tu búsqueda, o "Agregar todos de la sucursal" para seleccionar todos los colaboradores de la sucursal</li>
                <li><strong>Redundancia:</strong> El jefe no puede ser empleado y viceversa</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formik.isValid}
          >
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin me-2"></i>
                Guardando...
              </>
            ) : (
              <>
                <i className="fa fa-save me-2"></i>
                {isEditing ? "Actualizar" : "Crear"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DirectBossForm;
